import { useQuery } from '@apollo/client/react';
import {
  DimeAlert,
  DimeAlertDescription,
  DimeAlertTitle,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeTreeTable,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Layers, TriangleAlertIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ListEmptyState } from '../../components/ListEmptyState.js';
import { useDeniedTooltip } from '../../lib/access/index.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_PRODUCT_TREE_BRANDS,
  type BrandOption,
  type GetProductTreeBrandsQueryResult,
  type GetProductTreeBrandsQueryVariables,
} from '../../modules/product-trees/operations.js';
import { ProductTreeBrandFilter } from '../../modules/product-trees/product-tree-brand-filter.js';
import { makeProductTreeColumns } from '../../modules/product-trees/product-tree-columns.js';
import { ProductTreeCreateDialog } from '../../modules/product-trees/product-tree-create-dialog.js';
import { useProductTreeNodes } from '../../modules/product-trees/use-product-tree-nodes.js';

export const Route = createFileRoute('/product-categories/')({
  component: ProductCategoriesPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function ProductCategoriesPage() {
  const navigate = useNavigate();
  // Empty selection means "every accessible brand" -- narrowed by ProductTreeBrandFilter, never
  // a gate the page waits behind (see use-product-tree-nodes.ts's doc comment).
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('product:tree');
  const deniedTooltip = useDeniedTooltip();

  const { data: brandData, loading: brandsLoading } = useQuery<
    GetProductTreeBrandsQueryResult,
    GetProductTreeBrandsQueryVariables
  >(GET_PRODUCT_TREE_BRANDS, { variables: { first: 500 } });
  const brands: BrandOption[] = useMemo(
    () => brandData?.productTreeBrands.edges.map((edge) => edge.node) ?? [],
    [brandData],
  );
  const allBrandIds = useMemo(() => brands.map((brand) => brand.id), [brands]);
  const effectiveBrandIds = brandFilter.length > 0 ? brandFilter : allBrandIds;

  // Same rule as sales-order-columns.tsx's showBrandColumn: a single accessible brand makes the
  // column and the filter pure noise, since every row would repeat the same value.
  const showBrandColumn = brands.length > 1;

  const { roots, getSubRows, loading, refetch, truncated } = useProductTreeNodes(effectiveBrandIds);

  const columns = useMemo(
    () =>
      makeProductTreeColumns(showBrandColumn, {
        canEdit,
        disabledTooltip: canEdit ? undefined : deniedTooltip('product:tree'),
        onSaved: () => void refetch(),
      }),
    [showBrandColumn, canEdit, deniedTooltip, refetch],
  );

  // Root-level only: with the list now browsing every accessible brand's tree at once (rather
  // than one brand at a time), a header-level "+ New Category" has no single row to inherit a
  // parent from the way PR 9's original click-to-select design did -- that selection gesture
  // now belongs to onRowClick's expand/collapse instead (item 42). A subcategory is still
  // reachable: the dialog's own Parent Category field lets the caller pick any category
  // directly. Pre-fills Brand only when the view is unambiguous (a single accessible brand, or
  // the filter has been narrowed to exactly one).
  const defaultBrandId = effectiveBrandIds.length === 1 ? effectiveBrandIds[0] : undefined;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">
          Product Categories
        </DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Browse your brands&apos; product category trees.
        </DimeCardDescription>
        <DimeCardAction className="flex items-end gap-2">
          <ProductTreeCreateDialog
            onCreated={() => void refetch()}
            defaultBrandId={defaultBrandId}
            disabled={!canEdit}
            disabledTooltip={canEdit ? undefined : deniedTooltip('product:tree')}
          />
        </DimeCardAction>
      </DimeCardHeader>

      {truncated && (
        <DimeAlert variant="warning" className="mb-4">
          <TriangleAlertIcon />
          <DimeAlertTitle>Showing a partial category tree</DimeAlertTitle>
          <DimeAlertDescription>
            This view is capped at 500 categories across the selected brands. Narrow the brand
            filter to see everything for a smaller set.
          </DimeAlertDescription>
        </DimeAlert>
      )}

      {!brandsLoading && brands.length === 0 ? (
        <ListEmptyState
          icon={<Layers />}
          title="No brands available"
          description="You don't have access to any brands yet."
        />
      ) : (
        <DimeTreeTable
          columns={columns}
          data={roots}
          getRowId={(node) => node.id}
          getSubRows={getSubRows}
          indentColumnId="name"
          isLoading={loading || brandsLoading}
          loadingRowCount={10}
          searchColumn="name"
          searchPlaceholder="Search categories…"
          filters={
            showBrandColumn ? (
              <ProductTreeBrandFilter
                brands={brands}
                value={brandFilter}
                onChange={setBrandFilter}
              />
            ) : undefined
          }
          showViewOptions
          showExpandAll
          // Supplying onRowClick at all is what makes DimeTreeTable treat a click anywhere on an
          // expandable row's row as toggle-expand, not just the chevron (see TreeTableRow.tsx).
          // onRowClick itself only ever fires for a leaf, so this never fires for a category
          // that still has children -- it navigates to the category's own detail view instead.
          onRowClick={(node) =>
            void navigate({
              to: '/product-categories/$productTreeId',
              params: { productTreeId: node.id },
            })
          }
          emptyMessage="No categories found."
        />
      )}
    </DimeCard>
  );
}
